import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Globe2 } from "lucide-react";
import ProfileCard from "@/components/ProfileCard";
import { publicApi } from "@/lib/account";
import { flagUrl, fromSlug, slugify } from "@/lib/countries";
import useSeo from "@/lib/seo";

export function CountriesIndex() {
  const [list, setList] = useState([]);
  useSeo("Global Marriage Community – Every Country", "Meet brides and grooms from 25+ countries. Cross-cultural, inter-racial and international marriages welcome on Vivaah Royale.");
  useEffect(() => { publicApi.get("/meta").then((r) => setList(r.data.countries)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 mandala-bg" data-testid="countries-page">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><Globe2 className="w-4 h-4" /> One world, one love</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3">Find love in every country</h1>
      <p className="text-slate-400 mt-4 max-w-2xl">Every nationality, faith and culture is welcome. Explore a country's community — or meet someone from the other side of the world.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-12">
        {list.map((c) => (
          <Link key={c.name} to={`/country/${slugify(c.name)}`} data-testid={`country-card-${slugify(c.name)}`} className="card-royal rounded-2xl p-5 flex flex-col gap-3 hover:-translate-y-1">
            {flagUrl(c.name) && <img src={flagUrl(c.name)} alt="" className="w-12 h-8 object-cover rounded shadow" loading="lazy" />}
            <span className="font-display text-xl leading-tight">{c.name}</span>
            <span className="text-xs text-slate-400">{c.count} profiles</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function CountryPage() {
  const { slug } = useParams();
  const country = fromSlug(slug);
  const [data, setData] = useState({ items: [], total: 0 });
  useSeo(`${country || "Global"} Marriage & Matrimony – Brides and Grooms`, `Meet ${country || ""} brides and grooms open to local and international marriage on Vivaah Royale.`);
  useEffect(() => { if (country) publicApi.get("/profiles", { params: { country, limit: 60 } }).then((r) => setData(r.data)); }, [country]);
  if (!country) return <p className="py-32 text-center text-slate-400" data-testid="country-not-found">Country not found. <Link to="/countries" className="text-amber-300 underline">All countries</Link></p>;
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="country-page">
      <Link to="/countries" className="text-sm text-slate-400 hover:text-amber-200" data-testid="country-back-link">← All countries</Link>
      <div className="flex items-center gap-4 mt-6">
        {flagUrl(country, 160) && <img src={flagUrl(country, 160)} alt="" className="w-16 h-11 object-cover rounded shadow" />}
        <h1 className="font-display text-4xl sm:text-5xl font-semibold" data-testid="country-title">{country}</h1>
      </div>
      <p className="text-slate-400 mt-4 max-w-2xl">{data.total} singles from {country}, open to love close to home or across borders. Join free and send interests at no cost.</p>
      <div className="flex gap-3 mt-6">
        <Link to="/me" className="btn-gold px-6 py-3 rounded-full" data-testid="country-join-button">Join free</Link>
        <Link to="/browse" className="btn-ghost px-6 py-3 rounded-full">Browse the world</Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
        {data.items.map((p, i) => <ProfileCard key={p.id} p={p} index={i} />)}
      </div>
      {data.items.length === 0 && <p className="text-slate-500 py-16 text-center">No profiles yet — be the first from {country}!</p>}
    </div>
  );
}
